import { useIsFocused } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { SystemBars } from 'react-native-edge-to-edge';
import { Button, Text } from 'react-native-paper';
import type { EdgeInsets } from 'react-native-safe-area-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ErrorSlogan, LanguageIcon, ShieldPersonIcon } from '@/assets';
import { BackgroundView } from '@/features/authorization/components/BackgroundView';
import { useAppState } from '@/features/authorization/provider/useAppState';
import {
  INVITE_URL,
  SUPPORT_URL
} from '@/features/profile-setting/constants/links';
import { useAppSettingStore } from '@/features/profile-setting/store/useAppSettingStore';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationButton } from '@/navigation/components/NavigationButton';
import { AppRoute } from '@/navigation/types/navigation';
import { OutlinedButton } from '@/shared/components/OutlinedButton';
import { StateView } from '@/shared/components/state-view/StateView';
import { isEmptyOrNull } from '@/shared/utils/string';
import { handleOpenUrl } from '@/shared/utils/url';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const makeStyles = (insets: EdgeInsets) =>
  StyleSheet.create({
    screen: {
      flex: 1,
      justifyContent: 'flex-start',
      paddingBottom: 32 + insets.bottom
    },
    image: { alignItems: 'center' },
    gradient: {
      gap: 20,
      flex: 1,
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    content: {
      gap: 16,
      paddingTop: 40,
      paddingHorizontal: 24,
      maxWidth: 600,
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    },
    description: {
      paddingBottom: 4,
      textAlign: 'center'
    },
    button: { minWidth: 190 },
    buttonRow: {
      gap: 16,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'wrap'
    }
  });

export const InvalidVersionScreen: FC = () => {
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(insets), [insets]);

  const { versionCheckResult } = useAppState();

  const { t } = useTranslation(LocalizationNameSpace.AUTHORIZATION, {
    keyPrefix: 'screens.invalid-version'
  });

  const update = useCallback(() => {
    const url = isEmptyOrNull(versionCheckResult?.url)
      ? INVITE_URL
      : versionCheckResult.url;
    handleOpenUrl(url);
  }, [versionCheckResult]);

  const selectedLocale = useAppSettingStore((state) => state.selectedLocale);
  const toggleLocale = useAppSettingStore((state) => state.toggleLocale);

  const isFocused = useIsFocused();

  useEffect(() => {
    if (isFocused)
      SystemBars.setStyle({ statusBar: 'light', navigationBar: 'dark' });
    else SystemBars.setStyle('dark');
  }, [isFocused]);

  return (
    <StateView
      backgroundColor={theme.colors.surfaceContainer}
      contentContainerStyle={styles.screen}
    >
      <BackgroundView>
        <ErrorSlogan />
      </BackgroundView>
      <LinearGradient
        style={styles.gradient}
        colors={[
          theme.colors.secondaryContainer,
          theme.colors.secondaryContainer,
          theme.colors.surfaceContainer
        ]}
      >
        <View style={styles.content}>
          <Text variant="titleMedium" style={styles.description}>
            {isEmptyOrNull(versionCheckResult?.text)
              ? t('description')
              : versionCheckResult.text}
          </Text>
          <Button mode="contained" style={styles.button} onPress={update}>
            {t('update')}
          </Button>
          <OutlinedButton
            style={styles.button}
            onPress={() => {
              handleOpenUrl(SUPPORT_URL);
            }}
          >
            {t('support')}
          </OutlinedButton>
        </View>
        <View style={styles.buttonRow}>
          <NavigationButton
            screen={AppRoute.Legal}
            mode="text"
            icon={({ color, size }) => (
              <ShieldPersonIcon fill={color} height={size} width={size} />
            )}
          >
            {t('legals')}
          </NavigationButton>
          <Button
            mode="text"
            icon={({ color, size }) => (
              <LanguageIcon fill={color} height={size} width={size} />
            )}
            onPress={toggleLocale}
          >
            {t(`availableLocale.${selectedLocale}`)}
          </Button>
        </View>
      </LinearGradient>
    </StateView>
  );
};
